import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useUserStore, useCartStore, useCompanyStore } from 'stores';
import { addItemCart } from 'api/ecommerce';
import {
  productNeedsConditionChoice,
  BOOK_CONDITION,
  buildAddItemCartPayload,
  bookConditionFlagsMissing,
  hydrateBookConditionFlags,
  normalizeAddItemCartResponse,
} from 'utils/bookCondition';

export function useAddToCartWithBookCondition() {
  const { t } = useTranslation(['cart']);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);

  const { isLogged, shippingAddress, isConcourseUser, info_concourse } = useUserStore();
  const { addItem } = useCartStore();
  const { shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping } = useCompanyStore();

  const executeAdd = useCallback(
    async (product, bookCondition) => {
      const province = shippingAddress?.province?.label || shippingAddress?.province || '';
      const concourseDiscount = isConcourseUser ? Number(info_concourse?.discount) || 15 : null;
      const needs = productNeedsConditionChoice(product);
      const resolvedCondition = needs ? bookCondition || BOOK_CONDITION.NEW : undefined;

      const addPayload = {
        product,
        province,
        shippingCost: shipping_cost,
        shippingCostIslands: shipping_cost_islands,
        minPurchaseForFreeShipping: min_purchase_for_free_shipping,
        isConcourseUser,
        concourseDiscount,
      };
      if (needs) {
        addPayload.bookCondition = resolvedCondition;
      }

      if (isLogged) {
        setLoading(true);
        const payload = buildAddItemCartPayload(product.id, 1, product, resolvedCondition);
        const { status, data } = await addItemCart(payload);
        if (status === 201) {
          const addedLine = normalizeAddItemCartResponse(data);
          const result = addItem({
            ...addPayload,
            product: addedLine?.unit_price != null
              ? {
                  ...product,
                  unit_price: addedLine.unit_price,
                  price: addedLine.unit_price,
                  book_condition: addedLine.book_condition ?? resolvedCondition,
                }
              : product,
            bookCondition: addedLine?.book_condition ?? addPayload.bookCondition,
          });
          if (!result?.success) {
            const errorMessage = result?.errorCode
              ? t(`cart:${result.errorCode}`, result.params || {})
              : t('cart:errorAddToCart');
            toast.error(errorMessage, { theme: 'colored' });
          }
        }
        setLoading(false);
      } else {
        const result = addItem(addPayload);
        if (!result?.success) {
          toast.error(result?.message || t('cart:errorAddToCart'), { theme: 'colored' });
        }
      }
    },
    [
      isLogged,
      shippingAddress,
      isConcourseUser,
      info_concourse,
      addItem,
      shipping_cost,
      shipping_cost_islands,
      min_purchase_for_free_shipping,
      t,
    ]
  );

  const requestAdd = useCallback(
    async (product) => {
      let enriched = product;
      if (bookConditionFlagsMissing(product)) {
        setHydrating(true);
        try {
          enriched = await hydrateBookConditionFlags(product);
        } finally {
          setHydrating(false);
        }
      }
      if (productNeedsConditionChoice(enriched)) {
        setPendingProduct(enriched);
      } else {
        await executeAdd(enriched, undefined);
      }
    },
    [executeAdd]
  );

  const confirmCondition = useCallback(
    async (bookCondition) => {
      const prod = pendingProduct;
      setPendingProduct(null);
      if (prod) await executeAdd(prod, bookCondition);
    },
    [pendingProduct, executeAdd]
  );

  const closeModal = useCallback(() => setPendingProduct(null), []);

  return {
    loading: loading || hydrating,
    pendingProduct,
    requestAdd,
    confirmCondition,
    closeModal,
    executeAdd,
  };
}
